import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationsContext';
import { useUserCounts } from '@/contexts/UserCountsContext';
import { listingsAPI, bidsAPI, watchlistAPI, ordersAPI, messagesAPI, usersAPI, reportsAPI, uploadsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ImageGallery } from '@/components/ui/image-gallery';
import { ReportImageUpload } from '@/components/reports/ReportImageUpload';
import { LikeButton } from '@/components/listings/LikeButton';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Clock,
    Heart,
    Share2,
    Package,
    Shield,
    Star,
    Gavel,
    ChevronLeft,
    ChevronRight,
    AlertTriangle,
    CheckCircle,
    Truck,
    Loader2,
    MessageCircle,
    Wifi,
    WifiOff,
    Zap,
    ZoomIn,
    Flag,
    Send,
    TrendingDown,
    Trash2,
    ImagePlus,
    X,
    Pencil,
    Check,
    GripVertical,
    ArrowLeftRight,
    Eye,
    Lock,
    Unlock,
} from 'lucide-react';
import { toast } from 'sonner';
import {
    formatCurrency,
    formatDate,
    formatDateTime,
    formatRelativeTime,
    getTimeRemaining,
    getConditionLabel,
    getInitials,
    getThumbnailUrl,
    getFullImageUrl,
    cn,
} from '@/lib/utils';

// Seller Avatar component with proper image fallback
const SellerAvatarLarge = ({ avatar, name, size = "w-14 h-14", textSize = "text-lg" }) => {
    const [imgError, setImgError] = React.useState(false);
    
    if (avatar && !imgError) {
        return (
            <div className={`${size} rounded-full overflow-hidden bg-primary flex items-center justify-center flex-shrink-0`}>
                <img 
                    src={getFullImageUrl(avatar)}
                    alt={name}
                    className="w-full h-full object-cover"
                    onError={() => setImgError(true)}
                />
            </div>
        );
    }
    
    return (
        <div className={`${size} rounded-full bg-primary flex items-center justify-center flex-shrink-0`}>
            <span className={`text-white ${textSize} font-medium`}>
                {getInitials(name)}
            </span>
        </div>
    );
};

// Sortable Image Thumbnail Component
const SortableImageThumbnail = ({ id, img, index, currentImageIndex, onSelect, canModify, onDelete, imagesCount }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 50 : 'auto',
        opacity: isDragging ? 0.8 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`relative group aspect-square ${isDragging ? 'ring-2 ring-primary shadow-lg' : ''}`}
        >
            {/* Drag handle - visible only when canModify */}
            {canModify && (
                <div
                    {...attributes}
                    {...listeners}
                    className="absolute top-1 left-1 z-10 w-6 h-6 rounded-md bg-black/50 text-white flex items-center justify-center cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity touch-none"
                    title="Trage pentru a reordona"
                >
                    <GripVertical className="w-4 h-4" />
                </div>
            )}
            
            <button
                onClick={() => onSelect(index)}
                className={`w-full h-full rounded-xl overflow-hidden border-2 transition-colors ${
                    index === currentImageIndex ? 'border-primary' : 'border-transparent'
                }`}
            >
                <img 
                    src={getThumbnailUrl(img, 100)} 
                    alt="" 
                    className="w-full h-full object-cover" 
                    loading="lazy"
                    draggable={false}
                />
            </button>
            
            {/* Delete button */}
            {canModify && imagesCount > 1 && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(index);
                    }}
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md z-20"
                    title="Șterge imaginea"
                >
                    <X className="w-3 h-3" />
                </button>
            )}
            
            {/* Index badge */}
            {canModify && (
                <div className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white text-xs flex items-center justify-center">
                    {index + 1}
                </div>
            )}
        </div>
    );
};

const ListingDetailPage = () => {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const { notifications, isConnected } = useNotifications();
    const { incrementCount, decrementCount } = useUserCounts();
    
    // Check if accessing from admin panel
    const isAdminAccess = searchParams.get('admin') === 'true';
    
    // Refs for LikeButtons to open modal
    const mobileLikeButtonRef = React.useRef(null);
    const desktopLikeButtonRef = React.useRef(null);
    const [currentLikesCount, setCurrentLikesCount] = React.useState(0);
    
    const [listing, setListing] = useState(null);
    const [bids, setBids] = useState([]);
    const [loading, setLoading] = useState(true);
    const [bidAmount, setBidAmount] = useState('');
    const [bidding, setBidding] = useState(false);
    const [isWatched, setIsWatched] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [buyDialogOpen, setBuyDialogOpen] = useState(false);
    const [isBuyNowPurchase, setIsBuyNowPurchase] = useState(false);
    const [shippingAddress, setShippingAddress] = useState('');
    const [purchasing, setPurchasing] = useState(false);
    const [contactDialogOpen, setContactDialogOpen] = useState(false);
    const [contactMessage, setContactMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [liveUpdate, setLiveUpdate] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [mainImageLoaded, setMainImageLoaded] = useState(false);
    const [hasTrackedView, setHasTrackedView] = useState(false);
    const [hasOrderOrWon, setHasOrderOrWon] = useState(false);
    
    // Wrapper to reset image loaded state when changing images
    const handleImageChange = useCallback((indexOrUpdater) => {
        setMainImageLoaded(false);
        setCurrentImageIndex(indexOrUpdater);
    }, []);

    const [sellerStats, setSellerStats] = useState(null);
    const [userOrder, setUserOrder] = useState(null);
    const [confirmingDelivery, setConfirmingDelivery] = useState(false);
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [reportReason, setReportReason] = useState('');
    const [reportDescription, setReportDescription] = useState('');
    const [submittingReport, setSubmittingReport] = useState(false);
    const [reportImages, setReportImages] = useState([]);
    const [uploadingReportImages, setUploadingReportImages] = useState(false);
    const [openedFromCard, setOpenedFromCard] = useState(false);
    const [reserveLoading, setReserveLoading] = useState(false);
    
    // Open report dialog if ?report=true is in URL
    useEffect(() => {
        if (searchParams.get('report') === 'true' && isAuthenticated && listing && user?.user_id !== listing.seller_id) {
            setReportDialogOpen(true);
            setOpenedFromCard(true);
        }
    }, [searchParams, isAuthenticated, listing, user?.user_id]);
    
    // Handle report dialog close - go back if opened from card
    const handleReportDialogClose = (open) => {
        setReportDialogOpen(open);
        if (!open && openedFromCard) {
            setOpenedFromCard(false);
            navigate(-1);
        }
    };
    
    // Message to seller state
    const [messageDialogOpen, setMessageDialogOpen] = useState(false);
    const [messageText, setMessageText] = useState('');
    const [sendingSellerMessage, setSendingSellerMessage] = useState(false);
    
    // Owner actions state (reduce price, delete, mark as sold)
    const [reducePriceOpen, setReducePriceOpen] = useState(false);
    const [newPrice, setNewPrice] = useState('');
    const [reducing, setReducing] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [markAsSoldDialogOpen, setMarkAsSoldDialogOpen] = useState(false);
    const [markingSold, setMarkingSold] = useState(false);
    
    // Add images state
    const [addImagesOpen, setAddImagesOpen] = useState(false);
    const [newImages, setNewImages] = useState([]);
    const [uploadingImages, setUploadingImages] = useState(false);
    const fileInputRef = React.useRef(null);
    
    // Edit title & description state (for admin/owner)
    const [editingTitle, setEditingTitle] = useState(false);
    const [editingDescription, setEditingDescription] = useState(false);
    const [editedTitle, setEditedTitle] = useState('');
    const [editedDescription, setEditedDescription] = useState('');
    const [savingTitle, setSavingTitle] = useState(false);
    const [savingDescription, setSavingDescription] = useState(false);
    
    // Edit return policy state
    const [editingReturnPolicy, setEditingReturnPolicy] = useState(false);
    const [savingReturnPolicy, setSavingReturnPolicy] = useState(false);
    
    // Reorder images state
    const [reorderingImages, setReorderingImages] = useState(false);
    
    // DnD sensors for desktop and mobile
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handle drag end for reordering images
    const handleDragEnd = async (event) => {
        const { active, over } = event;
        
        if (!over || active.id === over.id) return;
        
        const oldIndex = listing.images.findIndex((_, i) => `image-${i}` === active.id);
        const newIndex = listing.images.findIndex((_, i) => `image-${i}` === over.id);
        
        if (oldIndex === -1 || newIndex === -1) return;
        
        // Optimistic update
        const newImages = arrayMove(listing.images, oldIndex, newIndex);
        setListing(prev => ({ ...prev, images: newImages }));
        
        // Update current image index if needed
        if (currentImageIndex === oldIndex) {
            setCurrentImageIndex(newIndex);
        } else if (oldIndex < currentImageIndex && newIndex >= currentImageIndex) {
            setCurrentImageIndex(currentImageIndex - 1);
        } else if (oldIndex > currentImageIndex && newIndex <= currentImageIndex) {
            setCurrentImageIndex(currentImageIndex + 1);
        }
        
        // Save to backend
        setReorderingImages(true);
        try {
            await listingsAPI.reorderImages(listing.listing_id, newImages);
            toast.success('Ordinea imaginilor a fost actualizată');
        } catch (error) {
            // Revert on error
            setListing(prev => ({ ...prev, images: listing.images }));
            toast.error('Eroare la reordonarea imaginilor');
            console.error('Error reordering images:', error);
        } finally {
            setReorderingImages(false);
        }
    };

    // Handle sending message to seller
    const handleSendMessageToSeller = async () => {
        if (!isAuthenticated) {
            toast.error('Trebuie să fii autentificat pentru a trimite mesaje');
            navigate('/login');
            return;
        }
        
        if (!messageText.trim()) {
            toast.error('Scrie un mesaj');
            return;
        }
        
        setSendingSellerMessage(true);
        try {
            const res = await messagesAPI.send({
                receiver_id: listing.seller_id,
                listing_id: listing.listing_id,
                content: messageText.trim()
            });
            toast.success('Mesaj trimis către vânzător!');
            setMessageDialogOpen(false);
            setMessageText('');
            // Navigate to the conversation
            const convId = res.data?.conversation_id;
            if (convId) {
                navigate(`/messages/${convId}`);
            } else {
                // Fallback - go to messages page
                navigate('/messages');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la trimiterea mesajului');
        } finally {
            setSendingSellerMessage(false);
        }
    };

    // Handle title edit (admin/owner)
    const handleSaveTitle = async () => {
        if (!editedTitle.trim()) {
            toast.error('Titlul nu poate fi gol');
            return;
        }
        
        setSavingTitle(true);
        try {
            await listingsAPI.update(listing.listing_id, { title: editedTitle.trim() });
            setListing(prev => ({ ...prev, title: editedTitle.trim() }));
            setEditingTitle(false);
            toast.success('Titlu actualizat cu succes!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la actualizarea titlului');
        } finally {
            setSavingTitle(false);
        }
    };

    // Handle description edit (admin/owner)
    const handleSaveDescription = async () => {
        setSavingDescription(true);
        try {
            await listingsAPI.update(listing.listing_id, { description: editedDescription });
            setListing(prev => ({ ...prev, description: editedDescription }));
            setEditingDescription(false);
            toast.success('Descriere actualizată cu succes!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la actualizarea descrierii');
        } finally {
            setSavingDescription(false);
        }
    };

    // Handle return policy edit (owner only, if no orders/bids)
    const handleSaveReturnPolicy = async (newPolicy) => {
        setSavingReturnPolicy(true);
        try {
            await listingsAPI.update(listing.listing_id, { return_policy: newPolicy });
            setListing(prev => ({ ...prev, return_policy: newPolicy }));
            setEditingReturnPolicy(false);
            toast.success('Politica de returnare actualizată!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la actualizare');
        } finally {
            setSavingReturnPolicy(false);
        }
    };

    // Handle price reduction (owner only)
    const handleReducePrice = async () => {
        if (!listing || !newPrice) return;
        
        const priceNum = parseFloat(newPrice);
        const currentPrice = listing.listing_type === 'auction'
            ? (listing.current_price || listing.starting_price || 0)
            : (listing.price || listing.current_price || listing.buy_now_price || 0);
        
        if (priceNum >= currentPrice) {
            toast.error(`Noul preț trebuie să fie mai mic decât ${formatCurrency(currentPrice)}`);
            return;
        }
        
        if (priceNum <= 0) {
            toast.error('Prețul trebuie să fie mai mare decât 0');
            return;
        }
        
        setReducing(true);
        try {
            const response = await listingsAPI.reducePrice(listing.listing_id, priceNum);
            toast.success(response.data.message || 'Prețul a fost redus cu succes');
            
            // Update local state
            setListing(prev => ({
                ...prev,
                ...(prev.listing_type === 'auction' 
                    ? { starting_price: priceNum, current_price: priceNum }
                    : { buy_now_price: priceNum, price: priceNum, current_price: priceNum }
                ),
                previous_price: currentPrice,
                discount_percent: response.data.discount_percent
            }));
            
            setReducePriceOpen(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la reducerea prețului');
        } finally {
            setReducing(false);
        }
    };
    
    // Handle listing deletion (owner only)
    const handleDeleteListing = async () => {
        if (!listing) return;
        
        setDeleting(true);
        try {
            await listingsAPI.delete(listing.listing_id);
            toast.success('Anunțul a fost șters cu succes');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la ștergerea anunțului');
        } finally {
            setDeleting(false);
        }
    };

    // Handle mark as sold (owner only)
    const handleMarkAsSold = async () => {
        if (!listing) return;
        
        setMarkingSold(true);
        try {
            await listingsAPI.update(listing.listing_id, { status: 'sold' });
            toast.success('Piesa a fost marcată ca vândută');
            
            // Update local state
            setListing(prev => ({
                ...prev,
                status: 'sold'
            }));
            setMarkAsSoldDialogOpen(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la marcarea ca vândută');
        } finally {
            setMarkingSold(false);
        }
    };

    // Handle toggle reserve status (owner only)
    const handleToggleReserve = async () => {
        if (!listing) return;
        
        setReserveLoading(true);
        try {
            const response = await listingsAPI.toggleReserve(listing.listing_id);
            toast.success(response.data.message || (listing.is_reserved ? 'Piesa a fost eliberată' : 'Piesa a fost reținută'));
            
            // Update local state
            setListing(prev => ({
                ...prev,
                is_reserved: !prev.is_reserved
            }));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la actualizarea stării piesei');
        } finally {
            setReserveLoading(false);
        }
    };

    // Handle file selection for new images
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        const maxImages = 10;
        const currentCount = listing?.images?.length || 0;
        const remainingSlots = maxImages - currentCount;
        
        if (files.length > remainingSlots) {
            toast.error(`Poți adăuga maximum ${remainingSlots} imagini noi`);
            return;
        }
        
        // Create preview URLs
        const previews = files.map(file => ({
            file,
            preview: URL.createObjectURL(file)
        }));
        
        setNewImages(previews);
    };
    
    // Remove a preview image
    const removePreviewImage = (index) => {
        setNewImages(prev => {
            const updated = [...prev];
            URL.revokeObjectURL(updated[index].preview);
            updated.splice(index, 1);
            return updated;
        });
    };
    
    // Upload images and add to listing
    const handleAddImages = async () => {
        if (newImages.length === 0) {
            toast.error('Selectează cel puțin o imagine');
            return;
        }
        
        setUploadingImages(true);
        try {
            // Upload each image
            const uploadedUrls = [];
            for (const imgData of newImages) {
                const formData = new FormData();
                formData.append('file', imgData.file);
                const res = await uploadsAPI.uploadImage(formData);
                uploadedUrls.push(res.data.url);
            }
            
            // Add images to listing
            const response = await listingsAPI.addImages(listing.listing_id, uploadedUrls);
            toast.success(response.data.message);
            
            // Update local state
            setListing(prev => ({
                ...prev,
                images: response.data.images
            }));
            
            // Clear previews
            newImages.forEach(img => URL.revokeObjectURL(img.preview));
            setNewImages([]);
            setAddImagesOpen(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la încărcarea imaginilor');
        } finally {
            setUploadingImages(false);
        }
    };
    
    // Delete an image from listing
    const handleDeleteImage = async (imageIndex) => {
        if (!listing) return;
        
        try {
            const response = await listingsAPI.deleteImage(listing.listing_id, imageIndex);
            toast.success(response.data.message);
            
            // Update local state
            setListing(prev => ({
                ...prev,
                images: response.data.images
            }));
            
            // Reset current image index if needed
            if (currentImageIndex >= response.data.images.length) {
                handleImageChange(Math.max(0, response.data.images.length - 1));
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la ștergerea imaginii');
        }
    };

    // Auto-fill shipping address from user profile
    useEffect(() => {
        if (user?.shipping_address && !shippingAddress) {
            setShippingAddress(user.shipping_address);
        }
    }, [user?.shipping_address, shippingAddress]);

    // Scroll to top when page loads
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    // Refresh listing data
    const refreshListing = useCallback(async () => {
        try {
            const listingRes = await listingsAPI.getById(id, false);
            const listingData = listingRes.data;
            setListing(listingData);
            
            // Fetch bids using actual listing_id
            if (listingData.listing_type === 'auction') {
                const bidsRes = await bidsAPI.getByListing(listingData.listing_id);
                const bidsData = bidsRes.data || [];
                setBids(bidsData);
                
                // First bid = starting_price, subsequent bids = current_price + increment
                const minBid = bidsData.length > 0 
                    ? listingData.current_price + listingData.minimum_bid_increment
                    : listingData.starting_price || listingData.current_price;
                setBidAmount(minBid.toString());
            }
        } catch (error) {
            console.error('Error refreshing listing:', error);
        }
    }, [id]);

    // Listen for real-time bid updates
    useEffect(() => {
        if (!notifications.length) return;
        
        const lastNotification = notifications[0];
        if (lastNotification.type === 'new_bid' && lastNotification.data?.listing_id === id) {
            // Flash the live update indicator
            setLiveUpdate(true);
            setTimeout(() => setLiveUpdate(false), 2000);
            
            // Refresh the listing data
            refreshListing();
        }
    }, [notifications, id, refreshListing]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Track view only once per session (first load)
                const shouldTrackView = !hasTrackedView;
                // First fetch the listing to get the actual listing_id
                const listingRes = await listingsAPI.getById(id, shouldTrackView);
                const listingData = listingRes.data;
                setListing(listingData);
                
                if (shouldTrackView) {
                    setHasTrackedView(true);
                }
                
                // Now fetch bids using the actual listing_id (not the slug from URL)
                if (listingData.listing_type === 'auction') {
                    let bidsData = [];
                    try {
                        const bidsRes = await bidsAPI.getByListing(listingData.listing_id);
                        bidsData = bidsRes.data || [];
                        setBids(bidsData);
                    } catch (bidsError) {
                        console.error('Error fetching bids:', bidsError);
                        setBids([]);
                    }
                    
                    // Set initial bid amount
                    // First bid = starting_price, subsequent bids = current_price + increment
                    const minBid = bidsData.length > 0
                        ? listingData.current_price + listingData.minimum_bid_increment
                        : listingData.starting_price || listingData.current_price;
                    setBidAmount(minBid.toString());
                }
            } catch (error) {
                console.error('Error fetching listing:', error);
                toast.error('Eroare la încărcarea listării');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, hasTrackedView]);

    // Check if listing is in watchlist
    useEffect(() => {
        const checkWatchlist = async () => {
            if (!isAuthenticated || !listing) return;
            try {
                const res = await watchlistAPI.getMy();
                const watchedIds = res.data.map(w => w.listing_id);
                setIsWatched(watchedIds.includes(listing.listing_id));
            } catch (error) {
                console.error('Error checking watchlist:', error);
            }
        };
        checkWatchlist();
    }, [isAuthenticated, listing]);

    // Fetch seller stats
    useEffect(() => {
        const fetchSellerStats = async () => {
            if (!listing?.seller_id) return;
            try {
                const res = await usersAPI.getStats(listing.seller_id);
                setSellerStats(res.data);
            } catch (error) {
                console.error('Error fetching seller stats:', error);
            }
        };
        fetchSellerStats();
    }, [listing?.seller_id]);

    // Check if user has an order or won the auction for this listing
    useEffect(() => {
        const checkOrderOrWinner = async () => {
            if (!isAuthenticated || !listing) return;
            
            try {
                // Check if user has an order for this listing
                const ordersRes = await ordersAPI.getMy();
                const order = ordersRes.data.find(o => o.listing_id === listing.listing_id);
                
                if (order) {
                    setUserOrder(order);
                    setHasOrderOrWon(true);
                } else {
                    // Check if user won the auction (is the highest bidder on ended auction)
                    const isAuctionWinner = listing.listing_type === 'auction' && 
                        listing.status === 'sold' && 
                        bids.length > 0 && 
                        bids[0]?.bidder_id === user?.user_id;
                    
                    setHasOrderOrWon(isAuctionWinner);
                }
            } catch (error) {
                console.error('Error checking order status:', error);
            }
        };
        
        checkOrderOrWinner();
    }, [isAuthenticated, listing, bids, user?.user_id]);

    // Handle confirm delivery
    const handleConfirmDelivery = async () => {
        if (!userOrder) return;
        
        setConfirmingDelivery(true);
        try {
            await ordersAPI.confirmDelivery(userOrder.order_id);
            toast.success('Primirea coletului a fost confirmată!');
            setUserOrder(prev => ({ ...prev, order_status: 'delivered' }));
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la confirmarea primirii');
        } finally {
            setConfirmingDelivery(false);
        }
    };

    // Update time remaining
    useEffect(() => {
        if (!listing?.end_date) return;

        const updateTime = () => {
            setTimeRemaining(getTimeRemaining(listing.end_date));
        };

        updateTime();
        const interval = setInterval(updateTime, 1000);
        return () => clearInterval(interval);
    }, [listing?.end_date]);

    const handlePlaceBid = async () => {
        if (!isAuthenticated) {
            toast.error('Trebuie să fii autentificat pentru a licita');
            navigate('/login');
            return;
        }

        const amount = parseFloat(bidAmount);
        // First bid = starting_price, subsequent bids = current_price + increment
        const minBid = bids.length > 0
            ? listing.current_price + listing.minimum_bid_increment
            : listing.starting_price || listing.current_price;

        if (amount < minBid) {
            toast.error(`Licitația minimă este ${formatCurrency(minBid)}`);
            return;
        }

        setBidding(true);
        try {
            // Use listing.listing_id instead of URL id (which might be a slug)
            await bidsAPI.place({ listing_id: listing.listing_id, amount });
            toast.success('Licitație plasată cu succes!');
            
            // Refresh listing and bids
            const [listingRes, bidsRes] = await Promise.all([
                listingsAPI.getById(id, false),
                bidsAPI.getByListing(listing.listing_id),
            ]);
            setListing(listingRes.data);
            const newBidsData = bidsRes.data || [];
            setBids(newBidsData);
            // After placing a bid, there's at least one bid now, so use current_price + increment
            setBidAmount((listingRes.data.current_price + listingRes.data.minimum_bid_increment).toString());
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la plasarea licitației');
        } finally {
            setBidding(false);
        }
    };

    const handleBuyNow = async () => {
        if (!isAuthenticated) {
            toast.error('Trebuie să fii autentificat pentru a cumpăra');
            navigate('/login');
            return;
        }

        if (!shippingAddress.trim()) {
            toast.error('Introdu adresa de livrare');
            return;
        }

        setPurchasing(true);
        try {
            const orderRes = await ordersAPI.create({
                listing_id: listing.listing_id,
                shipping_address: shippingAddress,
                buy_now: isBuyNowPurchase,
            });
            toast.success('Comandă creată cu succes!');
            navigate(`/orders/${orderRes.data.order_id}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la crearea comenzii');
        } finally {
            setPurchasing(false);
            setBuyDialogOpen(false);
            setIsBuyNowPurchase(false);
        }
    };

    const toggleWatchlist = async () => {
        if (!isAuthenticated) {
            toast.error('Trebuie să fii autentificat');
            navigate('/login');
            return;
        }

        try {
            // Use listing.listing_id, not URL id (which might be a slug)
            if (isWatched) {
                await watchlistAPI.remove(listing.listing_id);
                setIsWatched(false);
                decrementCount('favorites');
                toast.success('Eliminat din favorite');
            } else {
                await watchlistAPI.add(listing.listing_id);
                setIsWatched(true);
                incrementCount('favorites');
                toast.success('Adăugat la favorite');
            }
        } catch (error) {
            toast.error('Eroare la actualizarea listei');
        }
    };

    const handleContactSeller = async () => {
        if (!isAuthenticated) {
            toast.error('Trebuie să fii autentificat');
            navigate('/login');
            return;
        }

        if (!contactMessage.trim()) {
            toast.error('Scrie un mesaj');
            return;
        }

        setSendingMessage(true);
        try {
            const res = await messagesAPI.send({
                receiver_id: listing.seller_id,
                listing_id: listing.listing_id,
                content: contactMessage.trim()
            });
            toast.success('Mesaj trimis!');
            setContactDialogOpen(false);
            setContactMessage('');
            navigate(`/messages/${res.data.conversation_id}`);
        } catch (error) {
            toast.error('Eroare la trimiterea mesajului');
        } finally {
            setSendingMessage(false);
        }
    };

    const handleReport = async () => {
        if (!isAuthenticated) {
            toast.error('Trebuie să fii autentificat pentru a raporta');
            navigate('/login');
            return;
        }

        if (!reportReason) {
            toast.error('Selectează un motiv pentru raport');
            return;
        }

        setSubmittingReport(true);
        try {
            await reportsAPI.createWithImages({
                report_type: 'listing',
                target_id: listing.listing_id,
                target_title: listing.title,
                target_user_id: listing.seller_id,
                reason: reportReason,
                description: reportDescription.trim(),
            }, reportImages);
            toast.success('Raportul a fost trimis. Mulțumim!');
            setReportReason('');
            setReportDescription('');
            // Clean up image previews
            reportImages.forEach(img => URL.revokeObjectURL(img.preview));
            setReportImages([]);
            // Close dialog and navigate back if opened from card
            handleReportDialogClose(false);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la trimiterea raportului');
        } finally {
            setSubmittingReport(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background py-8">
                <div className="container mx-auto px-4 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-8">
                        <Skeleton className="aspect-square rounded-3xl" />
                        <div className="space-y-4">
                            <Skeleton className="h-8 w-3/4" />
                            <Skeleton className="h-6 w-1/2" />
                            <Skeleton className="h-32" />
                            <Skeleton className="h-12 w-full" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!listing) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <h2 className="font-heading text-2xl font-bold mb-2">Listare negăsită</h2>
                    <p className="text-muted-foreground mb-4">Această listare nu există sau a fost ștearsă</p>
                    <Button onClick={() => navigate('/')} className="rounded-full">
                        Înapoi acasă
                    </Button>
                </div>
            </div>
        );
    }

    const isAuction = listing.listing_type === 'auction';
    const isOwner = user?.user_id === listing.seller_id;
    const isAdminOrMod = user?.role === 'admin' || user?.role === 'moderator';
    // Admin/Mod can only edit when accessing from admin panel (with ?admin=true parameter)
    const canEdit = isOwner || (isAdminOrMod && isAdminAccess);
    // Allow modifications if listing is not sold (can edit active, ended, or expired auctions)
    const canModifyListing = canEdit && listing.status !== 'sold';
    const isReserved = listing.is_reserved;
    const canBid = isAuction && listing.status === 'active' && !isOwner && !timeRemaining?.expired && !isReserved;
    const canBuyNow = listing.buy_now_price && listing.status === 'active' && !isOwner && !isReserved;
    const canPurchase = listing.listing_type === 'direct_sale' && listing.status === 'active' && !isOwner && !isReserved;

    // Generate full image URL for Open Graph (must be absolute URL for social sharing)
    const baseUrl = 'https://www.colectarium.ro';
    // Use the dedicated OG image endpoint for properly sized social sharing images
    const ogImageUrl = `${baseUrl}/api/og-image/${listing.listing_id}`;
    const ogUrl = `${baseUrl}/listing/${listing.slug || listing.listing_id}`;
    const ogDescription = listing.description?.substring(0, 160) || `${listing.listing_type === 'auction' ? 'Licitație' : 'Vânzare'} pe Colectarium`;
    
    // SEO keywords based on listing
    const seoKeywords = [
        listing.listing_type === 'auction' ? 'licitație' : 'vânzare',
        listing.category_name?.toLowerCase(),
        listing.subcategory_name?.toLowerCase(),
        'cumpăr', 'vând', 'piese colecție', 'colectarium'
    ].filter(Boolean).join(', ');
    
    // JSON-LD structured data for the product
    const productSchema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": listing.title,
        "description": listing.description || ogDescription,
        "image": listing.images || [],
        "sku": listing.listing_id,
        "brand": { "@type": "Brand", "name": "Colectarium" },
        "offers": {
            "@type": listing.listing_type === 'auction' ? "AggregateOffer" : "Offer",
            "url": ogUrl,
            "priceCurrency": "RON",
            "price": listing.current_price || listing.starting_price || listing.buy_now_price || 0,
            "availability": listing.status === 'active' ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
            "seller": { "@type": "Person", "name": listing.seller_username || listing.seller_name }
        },
        "category": listing.category_name,
        "itemCondition": listing.condition === 'new' ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition"
    };
    
    // Breadcrumb JSON-LD schema
    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Acasă", "item": "https://colectarium.ro/" },
            { "@type": "ListItem", "position": 2, "name": listing.category_name, "item": `https://colectarium.ro/category/${listing.category_id}` },
            ...(listing.subcategory_name ? [{ "@type": "ListItem", "position": 3, "name": listing.subcategory_name, "item": `https://colectarium.ro/category/${listing.category_id}/${listing.subcategory_id}` }] : []),
            { "@type": "ListItem", "position": listing.subcategory_name ? 4 : 3, "name": listing.title, "item": ogUrl }
        ]
    };

    return (
        <div className="min-h-screen bg-background" data-testid="listing-detail-page">
            {/* SEO Meta Tags for Social Sharing */}
            <Helmet>
                <title>{listing.title} | {listing.listing_type === 'auction' ? 'Licitație' : 'Vânzare'} | Colectarium</title>
                <meta name="description" content={ogDescription} />
                <meta name="keywords" content={seoKeywords} />
                <link rel="canonical" href={ogUrl} />
                
                {/* Open Graph / Facebook / WhatsApp */}
                <meta property="og:type" content="product" />
                <meta property="og:url" content={ogUrl} />
                <meta property="og:title" content={listing.title} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:image" content={ogImageUrl} />
                <meta property="og:image:secure_url" content={ogImageUrl} />
                <meta property="og:image:type" content="image/jpeg" />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content={listing.title} />
                <meta property="og:site_name" content="Colectarium" />
                <meta property="og:locale" content="ro_RO" />
                
                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content={ogUrl} />
                <meta name="twitter:title" content={listing.title} />
                <meta name="twitter:description" content={ogDescription} />
                <meta name="twitter:image" content={ogImageUrl} />
                
                {/* Product specific */}
                <meta property="product:price:amount" content={listing.current_price || listing.starting_price || listing.buy_now_price} />
                <meta property="product:price:currency" content="RON" />
                
                {/* JSON-LD Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify(productSchema)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
            </Helmet>

            {/* Breadcrumb */}
            <div className="bg-white border-b border-border/50">
                <div className="container mx-auto px-4 lg:px-8 py-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Link to="/" className="hover:text-primary">Acasă</Link>
                        <span>/</span>
                        <Link to={`/category/${listing.category_slug || listing.category_id}`} className="hover:text-primary">
                            {listing.category_name}
                        </Link>
                        {listing.subcategory_name && (listing.subcategory_slug || listing.subcategory_id) && (
                            <>
                                <span>/</span>
                                <Link to={`/category/${listing.category_slug || listing.category_id}/${listing.subcategory_slug || listing.subcategory_id}`} className="hover:text-primary">
                                    {listing.subcategory_name}
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 lg:px-8 py-8">
                {/* Title Section - Above everything */}
                <div className="mb-6">
                    <div className="space-y-3">
                        {editingTitle ? (
                            <div className="flex items-center gap-2">
                                <Input
                                    value={editedTitle}
                                    onChange={(e) => setEditedTitle(e.target.value)}
                                    className="text-xl font-bold flex-1 max-w-2xl"
                                    autoFocus
                                />
                                <Button
                                    size="sm"
                                    onClick={handleSaveTitle}
                                    disabled={savingTitle}
                                >
                                    {savingTitle ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setEditingTitle(false)}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        ) : (
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <h1 className="font-heading text-2xl md:text-3xl font-bold" data-testid="listing-title">
                                        {listing.title}
                                    </h1>
                                    {/* Category & Subcategory - clickable */}
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {listing.category_name && (
                                            <Link 
                                                to={`/category/${listing.category_slug || listing.category_id}`}
                                                className="hover:text-primary transition-colors"
                                            >
                                                {listing.category_name}
                                            </Link>
                                        )}
                                        {listing.subcategory_name && (listing.subcategory_slug || listing.subcategory_id) && (
                                            <>
                                                <span> / </span>
                                                <Link 
                                                    to={`/category/${listing.category_slug || listing.category_id}/${listing.subcategory_slug || listing.subcategory_id}`}
                                                    className="hover:text-primary transition-colors"
                                                >
                                                    {listing.subcategory_name}
                                                </Link>
                                            </>
                                        )}
                                    </p>
                                    {/* Badges row */}
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        <Badge 
                                            className={cn(
                                                "rounded-full font-medium text-xs px-2.5 py-0.5",
                                                isAuction 
                                                    ? "bg-blue-500 text-white" 
                                                    : "bg-green-500 text-white"
                                            )}
                                        >
                                            {isAuction ? 'Licitație' : 'Preț fix'}
                                        </Badge>
                                        {listing.accepts_trades && (
                                            <Badge className="rounded-full font-medium text-xs px-2.5 py-0.5 bg-orange-500 text-white">
                                                Acceptă schimburi
                                            </Badge>
                                        )}
                                        {listing.is_reserved && (
                                            <Badge className="rounded-full font-medium text-xs px-2.5 py-0.5 bg-yellow-500 text-black">
                                                <Lock className="w-3 h-3 mr-1" />
                                                Reținută
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                                {canModifyListing && (
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-8 w-8 p-0 shrink-0"
                                        onClick={() => {
                                            setEditedTitle(listing.title);
                                            setEditingTitle(true);
                                        }}
                                        title="Editează titlul"
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                    {/* Images */}
                    <div className="space-y-4">
                        <div className="relative aspect-square rounded-3xl overflow-hidden bg-gray-100 shadow-card group flex items-center justify-center">
                            {/* Main image - loads in background */}
                            <img
                                src={getThumbnailUrl(listing.images?.[currentImageIndex], 800) || 'https://images.unsplash.com/photo-1680994330269-993e90deeca3?w=800'}
                                alt={listing.title}
                                className="absolute inset-0 w-full h-full object-contain cursor-zoom-in"
                                onClick={() => setLightboxOpen(true)}
                                onLoad={() => setMainImageLoaded(true)}
                            />
                            
                            {/* Thumbnail overlay - loads faster, shows on top until main image loads */}
                            {!mainImageLoaded && listing.images?.[currentImageIndex] && (
                                <img
                                    src={getThumbnailUrl(listing.images?.[currentImageIndex], 100)}
                                    alt=""
                                    className="absolute inset-0 w-full h-full object-contain z-10 bg-gray-100 pointer-events-none"
                                    aria-hidden="true"
                                />
                            )}
                            
                            {/* Zoom button */}
                            <button
                                onClick={() => setLightboxOpen(true)}
                                className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70 z-20"
                                title="Click pentru imagine completă"
                            >
                                <ZoomIn className="w-5 h-5" />
                            </button>
                            
                            {/* Navigation */}
                            {listing.images?.length > 1 && (
                                <>
                                    <button
                                        onClick={() => handleImageChange(prev => prev === 0 ? listing.images.length - 1 : prev - 1)}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors z-20"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>
                                    <button
                                        onClick={() => handleImageChange(prev => prev === listing.images.length - 1 ? 0 : prev + 1)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors z-20"
                                    >
                                        <ChevronRight className="w-5 h-5" />
                                    </button>
                                </>
                            )}
                        </div>
                        
                        {/* Lightbox - loads FULL resolution images (no thumbnail param) */}
                        <ImageGallery
                            images={listing.images || []}
                            initialIndex={currentImageIndex}
                            isOpen={lightboxOpen}
                            onClose={() => setLightboxOpen(false)}
                        />

                        {/* Thumbnails - sortable for owner/admin */}
                        {listing.images?.length > 1 && (
                            <>
                                {canModifyListing ? (
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                <GripVertical className="w-3 h-3" />
                                                Trage pentru a reordona imaginile
                                            </p>
                                            {reorderingImages && (
                                                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                                            )}
                                        </div>
                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                            <SortableContext
                                                items={listing.images.map((_, i) => `image-${i}`)}
                                                strategy={rectSortingStrategy}
                                            >
                                                <div className="grid grid-cols-4 gap-2 w-full">
                                                    {listing.images.map((img, index) => (
                                                        <SortableImageThumbnail
                                                            key={`image-${index}`}
                                                            id={`image-${index}`}
                                                            img={img}
                                                            index={index}
                                                            currentImageIndex={currentImageIndex}
                                                            onSelect={handleImageChange}
                                                            canModify={canModifyListing}
                                                            onDelete={handleDeleteImage}
                                                            imagesCount={listing.images.length}
                                                        />
                                                    ))}
                                                </div>
                                            </SortableContext>
                                        </DndContext>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-4 gap-2 w-full">
                                        {listing.images.map((img, index) => (
                                            <div key={index} className="relative group aspect-square">
                                                <button
                                                    onClick={() => handleImageChange(index)}
                                                    className={`w-full h-full rounded-xl overflow-hidden border-2 transition-colors bg-gray-100 flex items-center justify-center ${
                                                        index === currentImageIndex ? 'border-primary' : 'border-transparent'
                                                    }`}
                                                >
                                                    <img 
                                                        src={getThumbnailUrl(img, 100)} 
                                                        alt="" 
                                                        className="max-w-full max-h-full object-contain" 
                                                        loading="lazy"
                                                    />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Details */}
                    <div className="space-y-6">
                        {/* Mobile action buttons - shown only on mobile, below thumbnails */}
                        <div className="lg:hidden">
                            <div className="flex flex-col gap-2">
                                {/* Combined likes and share container - mobile */}
                                <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                                    {/* Left: Likes count as link to modal */}
                                    <button
                                        onClick={() => {
                                            if (mobileLikeButtonRef.current) {
                                                mobileLikeButtonRef.current.openModal();
                                            }
                                        }}
                                        className={cn(
                                            "text-sm font-medium",
                                            currentLikesCount > 0 
                                                ? "text-primary hover:underline cursor-pointer" 
                                                : "text-muted-foreground cursor-default"
                                        )}
                                        disabled={currentLikesCount === 0}
                                    >
                                        {currentLikesCount} {currentLikesCount === 1 ? 'apreciere' : 'aprecieri'}
                                    </button>
                                    
                                    {/* Right: Report + Share + Like buttons */}
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-9 w-9 rounded-full text-orange-500 hover:text-orange-600"
                                            onClick={() => setReportDialogOpen(true)}
                                            title="Raportează"
                                        >
                                            <Flag className="w-5 h-5" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-9 w-9 rounded-full"
                                            onClick={async () => {
                                                const shareUrl = `https://www.colectarium.ro/listing/${listing.slug || listing.listing_id}`;
                                                if (navigator.share) {
                                                    try {
                                                        await navigator.share({ title: listing.title, url: shareUrl });
                                                    } catch (err) {
                                                        if (err.name !== 'AbortError') {
                                                            navigator.clipboard.writeText(shareUrl);
                                                            toast.success('Link copiat!');
                                                        }
                                                    }
                                                } else {
                                                    navigator.clipboard.writeText(shareUrl);
                                                    toast.success('Link copiat!');
                                                }
                                            }}
                                        >
                                            <Share2 className="w-5 h-5" />
                                        </Button>
                                        <LikeButton 
                                            ref={mobileLikeButtonRef}
                                            listingId={listing.listing_id} 
                                            initialLikesCount={listing.likes_count || 0}
                                            size="sm"
                                            showCount={false}
                                            onLikesCountChange={setCurrentLikesCount}
                                        />
                                    </div>
                                </div>
                                {/* Owner buttons - vertical layout */}
                                {canModifyListing && (
                                    <div className="flex flex-col gap-2 mt-2">
                                        <Dialog open={addImagesOpen} onOpenChange={setAddImagesOpen}>
                                            <DialogTrigger asChild>
                                                <Button variant="outline" size="sm" className="w-full justify-start rounded-lg text-sm text-blue-600">
                                                    <ImagePlus className="w-4 h-4 mr-2" />
                                                    Adaugă imagini
                                                </Button>
                                            </DialogTrigger>
                                        </Dialog>
                                        {listing.status === 'active' && !timeRemaining?.expired && listing.listing_type !== 'auction' && (
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                className="w-full justify-start rounded-lg text-sm text-orange-600"
                                                onClick={() => {
                                                    const currentPrice = listing.price || listing.current_price || listing.buy_now_price || 0;
                                                    setNewPrice(Math.floor(currentPrice * 0.9).toString());
                                                    setReducePriceOpen(true);
                                                }}
                                            >
                                                <TrendingDown className="w-4 h-4 mr-2" />
                                                Reducere preț
                                            </Button>
                                        )}
                                        {/* Reserve/Unreserve button for active listings */}
                                        {listing.status === 'active' && (
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                className={cn(
                                                    "w-full justify-start rounded-lg text-sm",
                                                    listing.is_reserved ? "text-green-600" : "text-yellow-600"
                                                )}
                                                onClick={handleToggleReserve}
                                                disabled={reserveLoading}
                                            >
                                                {reserveLoading ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : listing.is_reserved ? (
                                                    <Unlock className="w-4 h-4 mr-2" />
                                                ) : (
                                                    <Lock className="w-4 h-4 mr-2" />
                                                )}
                                                {listing.is_reserved ? 'Eliberează piesa' : 'Reține piesa'}
                                            </Button>
                                        )}
                                        {listing.status === 'active' && (
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                className="w-full justify-start rounded-lg text-sm text-primary"
                                                onClick={() => navigate(`/dashboard?tab=for-sale&edit=${listing.listing_id}`)}
                                            >
                                                <Pencil className="w-4 h-4 mr-2" />
                                                Editează anunțul
                                            </Button>
                                        )}
                                        {/* Mark as Sold Button */}
                                        {listing.status === 'active' && (
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                className="w-full justify-start rounded-lg text-sm text-green-600"
                                                onClick={() => setMarkAsSoldDialogOpen(true)}
                                            >
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                Marchează vândută
                                            </Button>
                                        )}
                                        <Button 
                                            variant="outline" 
                                            size="sm"
                                            className="w-full justify-start rounded-lg text-sm text-red-600"
                                            onClick={() => setDeleteDialogOpen(true)}
                                        >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Șterge anunțul
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Actions & Info (no title here - moved above) */}
                        <div>
                            <div className="space-y-3">
                                {/* Actions row - likes, share, like button on left; owner buttons on right */}
                                <div className="hidden lg:flex items-start justify-between gap-4">
                                    {/* Left side: Likes count and action icons */}
                                    <div className="flex items-center gap-4">
                                        {/* Likes count - clickable to open modal */}
                                        <button
                                            onClick={() => {
                                                if (desktopLikeButtonRef.current && (listing.likes_count > 0 || currentLikesCount > 0)) {
                                                    desktopLikeButtonRef.current.openModal();
                                                }
                                            }}
                                            className={cn(
                                                "text-sm text-muted-foreground transition-colors",
                                                (currentLikesCount > 0) && "hover:text-foreground hover:underline cursor-pointer"
                                            )}
                                            disabled={currentLikesCount === 0}
                                        >
                                            {currentLikesCount} {currentLikesCount === 1 ? 'apreciere' : 'aprecieri'}
                                        </button>
                                        
                                        {/* Action icons */}
                                        <div className="flex items-center gap-2">
                                            {/* Report button - only for authenticated non-owners */}
                                            {isAuthenticated && !isOwner && (
                                                <Dialog open={reportDialogOpen} onOpenChange={handleReportDialogClose}>
                                                    <DialogTrigger asChild>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="icon"
                                                            className="rounded-full text-orange-500 hover:text-orange-600 hover:bg-orange-50"
                                                            data-testid="report-listing-btn"
                                                            title="Raportează"
                                                        >
                                                            <Flag className="w-5 h-5" />
                                                        </Button>
                                                    </DialogTrigger>
                                                    <DialogContent>
                                                        <DialogHeader>
                                                            <DialogTitle className="flex items-center gap-2">
                                                                <Flag className="w-5 h-5 text-orange-500" />
                                                                Raportează anunțul
                                                            </DialogTitle>
                                                        </DialogHeader>
                                                        <div className="space-y-4 py-4">
                                                            <div className="space-y-2">
                                                                <Label htmlFor="report-reason">Motiv</Label>
                                                                <Select value={reportReason} onValueChange={setReportReason}>
                                                                    <SelectTrigger id="report-reason" data-testid="report-reason-select">
                                                                        <SelectValue placeholder="Selectează motivul" />
                                                                    </SelectTrigger>
                                                                    <SelectContent>
                                                                        <SelectItem value="fake">Anunț fals/înșelător</SelectItem>
                                                                        <SelectItem value="fraud">Posibilă fraudă</SelectItem>
                                                                        <SelectItem value="counterfeit">Produs contrafăcut</SelectItem>
                                                                        <SelectItem value="inappropriate">Conținut nepotrivit</SelectItem>
                                                                        <SelectItem value="stolen">Obiect posibil furat</SelectItem>
                                                                        <SelectItem value="wrong_category">Categorie greșită</SelectItem>
                                                                        <SelectItem value="spam">Spam</SelectItem>
                                                                        <SelectItem value="other">Altul</SelectItem>
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                            <div className="space-y-2">
                                                                <Label htmlFor="report-description">Descriere (opțional)</Label>
                                                                <Textarea
                                                                    id="report-description"
                                                                    value={reportDescription}
                                                                    onChange={(e) => setReportDescription(e.target.value)}
                                                                    placeholder="Descrie problema în detaliu..."
                                                                    rows={4}
                                                                    data-testid="report-description"
                                                                />
                                                            </div>
                                                            <ReportImageUpload
                                                                images={reportImages}
                                                                setImages={setReportImages}
                                                                uploading={uploadingReportImages}
                                                                setUploading={setUploadingReportImages}
                                                            />
                                                            <p className="text-xs text-muted-foreground">
                                                                Raportul tău va fi analizat de echipa noastră de moderare.
                                                                Rapoartele false pot duce la sancțiuni.
                                                            </p>
                                                        </div>
                                                        <DialogFooter>
                                                            <Button
                                                                variant="outline"
                                                                onClick={() => handleReportDialogClose(false)}
                                                                className="rounded-full"
                                                            >
                                                                Anulează
                                                            </Button>
                                                            <Button
                                                                onClick={handleReport}
                                                                disabled={submittingReport || !reportReason}
                                                                className="rounded-full bg-orange-500 hover:bg-orange-600"
                                                                data-testid="submit-report-btn"
                                                            >
                                                                {submittingReport ? (
                                                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                                ) : (
                                                                    <Flag className="w-4 h-4 mr-2" />
                                                                )}
                                                                Trimite raportul
                                                            </Button>
                                                        </DialogFooter>
                                                    </DialogContent>
                                                </Dialog>
                                            )}
                                            
                                            {/* Share button - icon only */}
                                            <Button 
                                                variant="ghost" 
                                                size="icon"
                                                className="rounded-full"
                                                title="Distribuie"
                                                onClick={async () => {
                                                    const shareUrl = `https://www.colectarium.ro/listing/${listing.slug || listing.listing_id}`;
                                                    if (navigator.share) {
                                                        try {
                                                            await navigator.share({
                                                                title: listing.title,
                                                                text: listing.description?.substring(0, 100) || `Vezi acest anunț pe Colectarium`,
                                                                url: shareUrl
                                                            });
                                                        } catch (err) {
                                                            if (err.name !== 'AbortError') {
                                                                navigator.clipboard.writeText(shareUrl);
                                                                toast.success('Link copiat în clipboard!');
                                                            }
                                                        }
                                                    } else {
                                                        navigator.clipboard.writeText(shareUrl);
                                                        toast.success('Link copiat în clipboard!');
                                                    }
                                                }}
                                            >
                                                <Share2 className="w-5 h-5" />
                                            </Button>
                                            
                                            {/* Like button - icon only */}
                                            <LikeButton 
                                                ref={desktopLikeButtonRef}
                                                listingId={listing.listing_id} 
                                                initialLikesCount={listing.likes_count || 0}
                                                size="sm"
                                                showCount={false}
                                                onLikesCountChange={setCurrentLikesCount}
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Right side: Owner/Admin actions - vertical layout */}
                                    {canModifyListing && (
                                        <div className="flex flex-col gap-2 items-end">
                                            {/* Add Images Button */}
                                            <Dialog open={addImagesOpen} onOpenChange={setAddImagesOpen}>
                                                <DialogTrigger asChild>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        className="justify-start rounded-lg text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                                        data-testid="add-images-btn"
                                                    >
                                                        <ImagePlus className="w-4 h-4 mr-2" />
                                                        Adaugă imagini
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent className="max-w-lg">
                                                    <DialogHeader>
                                                        <DialogTitle className="flex items-center gap-2">
                                                            <ImagePlus className="w-5 h-5 text-blue-500" />
                                                            Adaugă imagini
                                                        </DialogTitle>
                                                    </DialogHeader>
                                                    <p className="text-sm text-muted-foreground">
                                                        Adaugă imagini suplimentare la anunțul tău. Limita maximă este de 10 imagini.
                                                    </p>
                                                    <div className="space-y-4 py-4">
                                                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                                            <span className="text-sm text-muted-foreground">Imagini curente:</span>
                                                            <span className="font-semibold">{listing.images?.length || 0} / 10</span>
                                                        </div>
                                                        
                                                        {/* File input */}
                                                        <div className="space-y-2">
                                                            <Label>Selectează imagini</Label>
                                                            <input
                                                                ref={fileInputRef}
                                                                type="file"
                                                                accept="image/jpeg,image/png,image/gif,image/webp"
                                                                multiple
                                                                onChange={handleFileSelect}
                                                                className="hidden"
                                                            />
                                                            <Button
                                                                variant="outline"
                                                                className="w-full"
                                                                onClick={() => fileInputRef.current?.click()}
                                                                disabled={uploadingImages}
                                                            >
                                                                <ImagePlus className="w-4 h-4 mr-2" />
                                                                Alege fișiere
                                                            </Button>
                                                        </div>
                                                        
                                                        {/* Image previews */}
                                                        {newImages.length > 0 && (
                                                            <div className="space-y-2">
                                                                <Label>Imagini selectate ({newImages.length})</Label>
                                                                <div className="grid grid-cols-4 gap-2">
                                                                    {newImages.map((img, index) => (
                                                                        <div key={index} className="relative group">
                                                                            <img 
                                                                                src={img.preview} 
                                                                                alt={`Preview ${index + 1}`}
                                                                                className="w-full h-20 object-cover rounded-lg"
                                                                            />
                                                                            <button
                                                                                onClick={() => removePreviewImage(index)}
                                                                                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                                                            >
                                                                                <X className="w-3 h-3" />
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <DialogFooter>
                                                        <Button variant="outline" onClick={() => {
                                                            newImages.forEach(img => URL.revokeObjectURL(img.preview));
                                                            setNewImages([]);
                                                            setAddImagesOpen(false);
                                                        }}>
                                                            Anulează
                                                        </Button>
                                                        <Button 
                                                            onClick={handleAddImages}
                                                            disabled={uploadingImages || newImages.length === 0}
                                                            className="bg-blue-600 hover:bg-blue-700"
                                                        >
                                                            {uploadingImages && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                                            {uploadingImages ? 'Se încarcă...' : `Adaugă ${newImages.length} imagini`}
                                                        </Button>
                                                    </DialogFooter>
                                                </DialogContent>
                                            </Dialog>
                                            
                                            {/* Reduce Price Button - only for active non-auction listings that aren't expired */}
                                            {listing.status === 'active' && !timeRemaining?.expired && listing.listing_type !== 'auction' && (
                                            <Dialog open={reducePriceOpen} onOpenChange={setReducePriceOpen}>
                                                <DialogTrigger asChild>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        className="justify-start rounded-lg text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                                        data-testid="reduce-price-btn"
                                                        onClick={() => {
                                                            const currentPrice = listing.price || listing.current_price || listing.buy_now_price || 0;
                                                            setNewPrice(Math.floor(currentPrice * 0.9).toString());
                                                        }}
                                                    >
                                                        <TrendingDown className="w-4 h-4 mr-2" />
                                                        Reducere preț
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent>
                                                    <DialogHeader>
                                                        <DialogTitle className="flex items-center gap-2">
                                                            <TrendingDown className="w-5 h-5 text-orange-500" />
                                                            Reducere preț
                                                        </DialogTitle>
                                                    </DialogHeader>
                                                    <p className="text-sm text-muted-foreground">
                                                        Utilizatorii care au acest produs în favorite vor fi notificați despre reducere.
                                                    </p>
                                                    <div className="space-y-4 py-4">
                                                        <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                                            <span className="text-sm text-muted-foreground">Preț actual:</span>
                                                            <span className="font-semibold">
                                                                {formatCurrency(listing.price || listing.current_price || listing.buy_now_price || 0)}
                                                            </span>
                                                        </div>
                                                        
                                                        <div className="space-y-2">
                                                            <Label htmlFor="newPriceListing">Noul preț (RON)</Label>
                                                            <Input
                                                                id="newPriceListing"
                                                                type="number"
                                                                value={newPrice}
                                                                onChange={(e) => setNewPrice(e.target.value)}
                                                                placeholder="Introdu noul preț"
                                                                min="1"
                                                            />
                                                        </div>
                                                        
                                                        {newPrice && parseFloat(newPrice) > 0 && (
                                                            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                                                                <div className="flex justify-between items-center">
                                                                    <span className="text-sm text-orange-700">Reducere:</span>
                                                                    <span className="font-semibold text-orange-700">
                                                                        -{Math.round(
                                                                            ((
                                                                                (listing.listing_type === 'auction'
                                                                                    ? (listing.current_price || listing.starting_price || 0)
                                                                                    : (listing.price || listing.current_price || listing.buy_now_price || 0)
                                                                                ) - parseFloat(newPrice)
                                                                            ) / (
                                                                                listing.listing_type === 'auction'
                                                                                    ? (listing.current_price || listing.starting_price || 1)
                                                                                    : (listing.price || listing.current_price || listing.buy_now_price || 1)
                                                                            )) * 100
                                                                        )}%
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <DialogFooter>
                                                        <Button variant="outline" onClick={() => setReducePriceOpen(false)}>
                                                            Anulează
                                                        </Button>
                                                        <Button 
                                                            onClick={handleReducePrice}
                                                            disabled={reducing || !newPrice}
                                                            className="bg-orange-600 hover:bg-orange-700"
                                                        >
                                                            {reducing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                                            Aplică reducerea
                                                        </Button>
                                                    </DialogFooter>
                                                </DialogContent>
                                            </Dialog>
                                            )}
                                            
                                            {/* Reserve/Unreserve button for active listings */}
                                            {listing.status === 'active' && (
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    className={cn(
                                                        "justify-start rounded-lg",
                                                        listing.is_reserved 
                                                            ? "text-green-600 hover:text-green-700 hover:bg-green-50" 
                                                            : "text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                                                    )}
                                                    onClick={handleToggleReserve}
                                                    disabled={reserveLoading}
                                                >
                                                    {reserveLoading ? (
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    ) : listing.is_reserved ? (
                                                        <Unlock className="w-4 h-4 mr-2" />
                                                    ) : (
                                                        <Lock className="w-4 h-4 mr-2" />
                                                    )}
                                                    {listing.is_reserved ? 'Eliberează piesa' : 'Reține piesa'}
                                                </Button>
                                            )}
                                            
                                            {/* Edit Button - between Reduce and Delete */}
                                            {listing.status === 'active' && (
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    className="justify-start rounded-lg text-primary hover:text-primary/80 hover:bg-primary/10"
                                                    onClick={() => navigate(`/dashboard?tab=for-sale&edit=${listing.listing_id}`)}
                                                    data-testid="edit-listing-btn"
                                                >
                                                    <Pencil className="w-4 h-4 mr-2" />
                                                    Editează anunțul
                                                </Button>
                                            )}
                                            
                                            {/* Mark as Sold Button */}
                                            {listing.status === 'active' && (
                                                <Button 
                                                    variant="outline" 
                                                    size="sm"
                                                    className="justify-start rounded-lg text-green-600 hover:text-green-700 hover:bg-green-50"
                                                    onClick={() => setMarkAsSoldDialogOpen(true)}
                                                    data-testid="mark-sold-btn"
                                                >
                                                    <CheckCircle className="w-4 h-4 mr-2" />
                                                    Marchează vândută
                                                </Button>
                                            )}
                                            
                                            {/* Delete Button */}
                                            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                                                <DialogTrigger asChild>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        className="justify-start rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        data-testid="delete-listing-btn"
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Șterge anunțul
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent>
                                                    <DialogHeader>
                                                        <DialogTitle className="flex items-center gap-2 text-red-600">
                                                            <Trash2 className="w-5 h-5" />
                                                            Șterge anunțul
                                                        </DialogTitle>
                                                    </DialogHeader>
                                                    <p className="text-sm text-muted-foreground">
                                                        Ești sigur că vrei să ștergi anunțul &quot;<strong>{listing.title}</strong>&quot;? 
                                                        Această acțiune nu poate fi anulată.
                                                    </p>
                                                    <DialogFooter className="mt-4">
                                                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                                                            Anulează
                                                        </Button>
                                                        <Button 
                                                            variant="destructive"
                                                            onClick={handleDeleteListing}
                                                            disabled={deleting}
                                                        >
                                                            {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                                            Șterge definitiv
                                                        </Button>
                                                    </DialogFooter>
                                                </DialogContent>
                                            </Dialog>
                                            
                                            {/* Mark as Sold Confirmation Dialog */}
                                            <Dialog open={markAsSoldDialogOpen} onOpenChange={setMarkAsSoldDialogOpen}>
                                                <DialogContent>
                                                    <DialogHeader>
                                                        <DialogTitle className="flex items-center gap-2 text-green-600">
                                                            <CheckCircle className="w-5 h-5" />
                                                            Confirmă vânzarea
                                                        </DialogTitle>
                                                    </DialogHeader>
                                                    <div className="space-y-3 pt-2">
                                                        <p className="text-sm text-muted-foreground">
                                                            Ești sigur că piesa &quot;<strong>{listing.title}</strong>&quot; a fost vândută?
                                                        </p>
                                                        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                                                            <p className="font-medium mb-1">💡 Recomandare</p>
                                                            <p>
                                                                Pentru a avea piesa în evidența vânzărilor și a beneficia de sistemul de rating, 
                                                                îți recomandăm să finalizezi vânzarea prin circuitul platformei (cumpărătorul să plaseze comanda prin site).
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <DialogFooter className="mt-4">
                                                        <Button variant="outline" onClick={() => setMarkAsSoldDialogOpen(false)}>
                                                            Anulează
                                                        </Button>
                                                        <Button 
                                                            className="bg-green-600 hover:bg-green-700"
                                                            onClick={handleMarkAsSold}
                                                            disabled={markingSold}
                                                        >
                                                            {markingSold && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                                            Marchează ca vândută
                                                        </Button>
                                                    </DialogFooter>
                                                </DialogContent>
                                            </Dialog>
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                                <span>{formatRelativeTime(listing.created_at)}</span>
                                <span>•</span>
                                <span>{listing.views} vizualizări</span>
                            </div>
                            
                            {/* Condition Badge */}
                            <div className="mt-3">
                                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                                    <Package className="w-4 h-4 mr-1.5" />
                                    Stare: {getConditionLabel(listing.condition)}
                                </span>
                            </div>
                        </div>

                        {/* Price Card */}
                        <Card className={`rounded-2xl transition-all duration-300 ${liveUpdate ? 'ring-2 ring-green-500 ring-offset-2' : ''}`}>
                            <CardContent className="p-6">
                                {/* Live indicator */}
                                {isAuction && (
                                    <div className="flex items-center justify-between mb-3">
                                        <div className={`flex items-center gap-2 text-xs ${isConnected ? 'text-green-600' : 'text-muted-foreground'}`}>
                                            {isConnected ? (
                                                <>
                                                    <Wifi className="w-3 h-3" />
                                                    <span>Live</span>
                                                    {liveUpdate && (
                                                        <span className="flex items-center gap-1 text-green-600 animate-pulse">
                                                            <Zap className="w-3 h-3" />
                                                            Actualizat!
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    <WifiOff className="w-3 h-3" />
                                                    <span>Offline</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {isAuction ? (
                                    <>
                                        {/* Auction Timer */}
                                        {timeRemaining && (
                                            <div className={`flex items-center gap-2 mb-4 px-4 py-2 rounded-xl ${
                                                timeRemaining.expired ? 'bg-red-50 text-red-600' : 
                                                timeRemaining.urgent ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-primary'
                                            }`}>
                                                <Clock className="w-5 h-5" />
                                                <span className="font-mono font-semibold">
                                                    {timeRemaining.expired ? 'Licitație încheiată' : 
                                                        `${timeRemaining.days || 0}z ${timeRemaining.hours || 0}h ${timeRemaining.minutes || 0}m ${timeRemaining.seconds || 0}s`
                                                    }
                                                </span>
                                            </div>
                                        )}

                                        <div className="flex items-end justify-between mb-4">
                                            <div>
                                                <p className="text-sm text-muted-foreground">Preț curent</p>
                                                <p className="font-heading text-3xl font-bold text-foreground">
                                                    {formatCurrency(listing.current_price)}
                                                </p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm text-muted-foreground">{listing.bid_count || bids.length} licitații</p>
                                                <p className="text-sm">
                                                    {(listing.bid_count || bids.length) > 0 
                                                        ? `Increment: +${formatCurrency(listing.minimum_bid_increment)}`
                                                        : `Start: ${formatCurrency(listing.starting_price || listing.current_price)}`
                                                    }
                                                </p>
                                            </div>
                                        </div>

                                        {canBid && isAuthenticated && (
                                            <div className="flex gap-2">
                                                <Input
                                                    type="number"
                                                    value={bidAmount}
                                                    onChange={(e) => setBidAmount(e.target.value)}
                                                    min={(listing.bid_count || bids.length) > 0 
                                                        ? listing.current_price + listing.minimum_bid_increment 
                                                        : listing.starting_price || listing.current_price
                                                    }
                                                    step={listing.minimum_bid_increment}
                                                    className="rounded-xl"
                                                    data-testid="bid-input"
                                                />
                                                <Button
                                                    onClick={handlePlaceBid}
                                                    disabled={bidding}
                                                    className="rounded-full bg-primary hover:bg-primary/90 px-8"
                                                    data-testid="place-bid-btn"
                                                >
                                                    {bidding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gavel className="w-4 h-4 mr-2" />}
                                                    Licitează
                                                </Button>
                                            </div>
                                        )}
                                        
                                        {canBid && !isAuthenticated && (
                                            <Button
                                                onClick={() => navigate('/login')}
                                                className="w-full rounded-full bg-primary hover:bg-primary/90"
                                                data-testid="login-to-bid-btn"
                                            >
                                                Autentifică-te pentru a licita
                                            </Button>
                                        )}

                                        {canBuyNow && isAuthenticated && (
                                            <div className="mt-4 pt-4 border-t">
                                                <p className="text-sm text-muted-foreground mb-2">Cumpără acum</p>
                                                <Dialog open={buyDialogOpen && isBuyNowPurchase} onOpenChange={(open) => {
                                                    setBuyDialogOpen(open);
                                                    if (open) setIsBuyNowPurchase(true);
                                                    else setIsBuyNowPurchase(false);
                                                }}>
                                                    <DialogTrigger asChild>
                                                        <Button variant="outline" className="w-full rounded-full" onClick={() => setIsBuyNowPurchase(true)}>
                                                            Cumpără acum la {formatCurrency(listing.buy_now_price)}
                                                        </Button>
                                                    </DialogTrigger>
                                                    <DialogContent>
                                                        <DialogHeader>
                                                            <DialogTitle>Cumpără acum</DialogTitle>
                                                        </DialogHeader>
                                                        <div className="space-y-4 pt-4">
                                                            <div>
                                                                <label className="text-sm font-medium">Adresa de livrare</label>
                                                                <Textarea
                                                                    value={shippingAddress}
                                                                    onChange={(e) => setShippingAddress(e.target.value)}
                                                                    placeholder="Introdu adresa completă de livrare"
                                                                    className="mt-2"
                                                                />
                                                            </div>
                                                            <div className="flex justify-between text-sm">
                                                                <span>Preț produs</span>
                                                                <span>{formatCurrency(listing.buy_now_price)}</span>
                                                            </div>
                                                            <div className="flex justify-between text-sm">
                                                                <span>Transport</span>
                                                                <span>{formatCurrency(listing.shipping_cost)}</span>
                                                            </div>
                                                            <Separator />
                                                            <div className="flex justify-between font-semibold">
                                                                <span>Total</span>
                                                                <span>{formatCurrency(listing.buy_now_price + listing.shipping_cost)}</span>
                                                            </div>
                                                            <Button
                                                                onClick={handleBuyNow}
                                                                disabled={purchasing}
                                                                className="w-full rounded-full bg-primary"
                                                            >
                                                                {purchasing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                                                Confirmă comanda
                                                            </Button>
                                                        </div>
                                                    </DialogContent>
                                                </Dialog>
                                            </div>
                                        )}
                                        
                                        {canBuyNow && !isAuthenticated && (
                                            <div className="mt-4 pt-4 border-t">
                                                <p className="text-sm text-muted-foreground mb-2">Cumpără acum la {formatCurrency(listing.buy_now_price)}</p>
                                                <Button
                                                    onClick={() => navigate('/login')}
                                                    variant="outline"
                                                    className="w-full rounded-full"
                                                    data-testid="login-to-buy-now-btn"
                                                >
                                                    Autentifică-te pentru a cumpăra
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div className="mb-4">
                                            <p className="text-sm text-muted-foreground">Preț</p>
                                            <p className="font-heading text-3xl font-bold text-foreground">
                                                {formatCurrency(listing.current_price)}
                                            </p>
                                            {listing.shipping_cost > 0 && (
                                                <p className="text-sm text-muted-foreground mt-1">
                                                    + {formatCurrency(listing.shipping_cost)} transport
                                                </p>
                                            )}
                                        </div>

                                        {canPurchase && isAuthenticated && (
                                            <Dialog open={buyDialogOpen} onOpenChange={setBuyDialogOpen}>
                                                <DialogTrigger asChild>
                                                    <Button className="w-full rounded-full bg-primary hover:bg-primary/90" data-testid="buy-btn">
                                                        Cumpără acum
                                                    </Button>
                                                </DialogTrigger>
                                                <DialogContent>
                                                    <DialogHeader>
                                                        <DialogTitle>Cumpără acum</DialogTitle>
                                                    </DialogHeader>
                                                    <div className="space-y-4 pt-4">
                                                        <div>
                                                            <label className="text-sm font-medium">Adresa de livrare</label>
                                                            <Textarea
                                                                value={shippingAddress}
                                                                onChange={(e) => setShippingAddress(e.target.value)}
                                                                placeholder="Introdu adresa completă de livrare"
                                                                className="mt-2"
                                                            />
                                                        </div>
                                                        <div className="flex justify-between text-sm">
                                                            <span>Preț produs</span>
                                                            <span>{formatCurrency(listing.current_price)}</span>
                                                        </div>
                                                        <div className="flex justify-between text-sm">
                                                            <span>Transport</span>
                                                            <span>{formatCurrency(listing.shipping_cost)}</span>
                                                        </div>
                                                        <Separator />
                                                        <div className="flex justify-between font-semibold">
                                                            <span>Total</span>
                                                            <span>{formatCurrency(listing.current_price + listing.shipping_cost)}</span>
                                                        </div>
                                                        <Button
                                                            onClick={handleBuyNow}
                                                            disabled={purchasing}
                                                            className="w-full rounded-full bg-primary"
                                                        >
                                                            {purchasing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                                            Confirmă comanda
                                                        </Button>
                                                    </div>
                                                </DialogContent>
                                            </Dialog>
                                        )}
                                        
                                        {canPurchase && !isAuthenticated && (
                                            <Button
                                                onClick={() => navigate('/login')}
                                                className="w-full rounded-full bg-primary hover:bg-primary/90"
                                                data-testid="login-to-buy-btn"
                                            >
                                                Autentifică-te pentru a cumpăra
                                            </Button>
                                        )}
                                        
                                        {/* Reserved listing message */}
                                        {listing.is_reserved && !isOwner && (
                                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                                                <div className="flex items-center gap-2">
                                                    <Lock className="w-4 h-4" />
                                                    <span className="font-medium">Piesă reținută</span>
                                                </div>
                                                <p className="mt-1 text-yellow-700">
                                                    Această piesă este momentan reținută de vânzător și nu poate fi cumpărată.
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>

                        {/* Order Status & Confirm Delivery Button */}
                        {userOrder && (
                            <Card className="rounded-2xl border-2 border-primary/20 bg-primary/5">
                                <CardContent className="p-4">
                                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                                        <Package className="w-5 h-5 text-primary" />
                                        Comanda ta
                                    </h3>
                                    
                                    <div className="space-y-2 text-sm mb-4">
                                        <div className="flex justify-between">
                                            <span className="text-muted-foreground">Status:</span>
                                            <Badge variant={
                                                userOrder.order_status === 'delivered' || userOrder.order_status === 'completed' ? 'default' :
                                                userOrder.order_status === 'shipped' ? 'secondary' :
                                                userOrder.order_status === 'paid' ? 'outline-solid' : 'destructive'
                                            }>
                                                {userOrder.order_status === 'pending_payment' && 'Așteaptă plata'}
                                                {userOrder.order_status === 'paid' && 'Plătit - Așteaptă expediere'}
                                                {userOrder.order_status === 'shipped' && 'Expediat'}
                                                {userOrder.order_status === 'delivered' && 'Livrat'}
                                                {userOrder.order_status === 'completed' && 'Finalizat'}
                                                {userOrder.order_status === 'cancelled' && 'Anulat'}
                                            </Badge>
                                        </div>
                                        {userOrder.tracking_number && (
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">AWB:</span>
                                                <span className="font-mono">{userOrder.tracking_number}</span>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {userOrder.order_status === 'shipped' && (
                                        <Button
                                            onClick={handleConfirmDelivery}
                                            disabled={confirmingDelivery}
                                            className="w-full rounded-full bg-green-600 hover:bg-green-700"
                                            data-testid="confirm-delivery-btn"
                                        >
                                            {confirmingDelivery ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                            ) : (
                                                <CheckCircle className="w-4 h-4 mr-2" />
                                            )}
                                            Confirm că am primit coletul
                                        </Button>
                                    )}
                                    
                                    {userOrder.order_status === 'pending_payment' && (
                                        <Button
                                            onClick={() => navigate(`/order/${userOrder.order_id}`)}
                                            className="w-full rounded-full"
                                        >
                                            Finalizează plata
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                        {/* Info Cards - Compact */}
                        <div className="grid grid-cols-2 gap-2">
                            <div className="p-3 rounded-xl bg-gray-50">
                                <Package className="w-4 h-4 text-primary mb-1" />
                                <p className="text-xs font-medium">Stare</p>
                                <p className="text-xs text-muted-foreground">{getConditionLabel(listing.condition)}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-gray-50">
                                <Truck className="w-4 h-4 text-primary mb-1" />
                                <p className="text-xs font-medium">Transport</p>
                                <p className="text-xs text-muted-foreground">
                                    {listing.shipping_cost === 0 ? 'Gratuit' : formatCurrency(listing.shipping_cost)}
                                </p>
                            </div>
                            <div className="p-3 rounded-xl bg-gray-50 relative group">
                                <CheckCircle className="w-4 h-4 text-primary mb-1" />
                                <p className="text-xs font-medium">Retur</p>
                                {editingReturnPolicy ? (
                                    <div className="flex flex-col gap-1 mt-1">
                                        <select
                                            className="text-xs border rounded px-2 py-1"
                                            defaultValue={listing.return_policy}
                                            onChange={(e) => handleSaveReturnPolicy(e.target.value)}
                                            disabled={savingReturnPolicy}
                                        >
                                            <option value="no_returns">Nu acceptă retururi</option>
                                            <option value="14_days">14 zile retur</option>
                                            <option value="30_days">30 zile retur</option>
                                        </select>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setEditingReturnPolicy(false)}
                                            className="text-xs h-6"
                                        >
                                            Anulează
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        <p className="text-xs text-muted-foreground">
                                            {listing.return_policy === 'no_returns' ? 'Nu acceptă' : 
                                             listing.return_policy === '14_days' ? '14 zile' : 
                                             listing.return_policy === '30_days' ? '30 zile' : 'Acceptat'}
                                        </p>
                                        {isOwner && listing.status === 'active' && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setEditingReturnPolicy(true)}
                                                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-5 w-5 p-0"
                                                title="Modifică politica de retur"
                                            >
                                                <Pencil className="w-3 h-3" />
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                            
                            {/* Total Price Card - Compact */}
                            <div className="p-3 rounded-xl bg-primary/10">
                                <p className="text-xs font-medium text-primary mb-1">Total</p>
                                <p className="text-base font-bold text-primary">
                                    {formatCurrency(
                                        (listing.listing_type === 'auction' 
                                            ? (listing.current_price || listing.starting_price || 0)
                                            : (listing.price || listing.current_price || listing.buy_now_price || 0)
                                        ) + (listing.shipping_cost || 0)
                                    )}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    {listing.listing_type === 'auction' 
                                        ? `${formatCurrency(listing.current_price || listing.starting_price || 0)} + ${listing.shipping_cost === 0 ? 'gratuit' : formatCurrency(listing.shipping_cost)}`
                                        : `${formatCurrency(listing.price || listing.current_price || listing.buy_now_price || 0)} + ${listing.shipping_cost === 0 ? 'gratuit' : formatCurrency(listing.shipping_cost)}`
                                    }
                                </p>
                            </div>
                            
                            {/* Accepts Trades - full width */}
                            {listing.accepts_trades && (
                                <div className="p-3 rounded-xl bg-blue-50 col-span-2">
                                    <div className="flex items-center gap-2 text-xs">
                                        <ArrowLeftRight className="w-4 h-4 text-blue-500" />
                                        <span className="font-medium text-blue-700">Acceptă schimburi</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Description Card - First */}
                        <Card className="rounded-2xl">
                            <CardHeader className="pb-2">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="font-heading text-base">Descriere</CardTitle>
                                    {canModifyListing && !editingDescription && (
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => {
                                                setEditedDescription(listing.description || '');
                                                setEditingDescription(true);
                                            }}
                                            title="Editează descrierea"
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                                {editingDescription ? (
                                    <div className="space-y-3">
                                        <Textarea
                                            value={editedDescription}
                                            onChange={(e) => setEditedDescription(e.target.value)}
                                            rows={6}
                                            className="w-full"
                                            placeholder="Descrierea piesei..."
                                        />
                                        <div className="flex gap-2 justify-end">
                                            <Button
                                                variant="ghost"
                                                onClick={() => setEditingDescription(false)}
                                            >
                                                Anulează
                                            </Button>
                                            <Button
                                                onClick={handleSaveDescription}
                                                disabled={savingDescription}
                                            >
                                                {savingDescription ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                                                Salvează
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="whitespace-pre-wrap text-muted-foreground text-sm">
                                        {listing.description}
                                    </p>
                                )}
                            </CardContent>
                        </Card>

                        {/* Seller Card - Compact */}
                        <Card className="rounded-2xl">
                            <CardHeader className="pb-1 pt-3 px-4">
                                <CardTitle className="text-xs font-medium text-muted-foreground">Vânzător</CardTitle>
                            </CardHeader>
                            <CardContent className="px-4 pb-3 pt-0">
                                <div className="flex items-center gap-3 mb-3">
                                    <SellerAvatarLarge 
                                        avatar={listing.seller_avatar}
                                        name={listing.seller_name || listing.seller_username}
                                    />
                                    <div className="flex-1">
                                        <Link to={`/user/${listing.seller_username?.trim()}`} className="font-semibold hover:text-primary text-sm">
                                            {listing.seller_name || listing.seller_username}
                                        </Link>
                                        {listing.seller_badges?.includes('verified') && (
                                            <Badge className="ml-2 bg-green-50 text-green-700 text-xs">Verificat</Badge>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Compact Seller Statistics */}
                                <div className="grid grid-cols-3 gap-1 mb-3 p-2 rounded-lg bg-muted/30">
                                    <div className="text-center">
                                        <div className="flex items-center justify-center gap-1 text-sm font-bold text-yellow-500">
                                            <Star className="w-3 h-3 fill-current" />
                                            {listing.seller_rating > 0 ? listing.seller_rating.toFixed(1) : '-'}
                                        </div>
                                        <div className="text-xs text-muted-foreground">Rating</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-sm font-bold text-primary">
                                            {sellerStats ? ((sellerStats.stats?.items_sold || 0) + (sellerStats.stats?.auctions_organized || 0)) : '-'}
                                        </div>
                                        <div className="text-xs text-muted-foreground">Vândute</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-sm font-bold text-blue-600">
                                            {sellerStats ? ((sellerStats.stats?.items_bought || 0) + (sellerStats.stats?.auctions_won || 0)) : '-'}
                                        </div>
                                        <div className="text-xs text-muted-foreground">Cumpărate</div>
                                    </div>
                                </div>
                                
                                {/* Contact Seller Button - show for all non-owners */}
                                {!isOwner && isAuthenticated && (
                                    <Dialog open={messageDialogOpen} onOpenChange={setMessageDialogOpen}>
                                        <DialogTrigger asChild>
                                            <Button className="rounded-full w-auto text-sm h-8" data-testid="contact-seller-btn">
                                                <Send className="w-3 h-3 mr-2" />
                                                Trimite mesaj
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Trimite mesaj către {listing.seller_name || listing.seller_username}</DialogTitle>
                                            </DialogHeader>
                                            <div className="py-4">
                                                <p className="text-sm text-muted-foreground mb-3">
                                                    Despre: <span className="font-medium text-foreground">{listing.title}</span>
                                                </p>
                                                <Textarea
                                                    value={messageText}
                                                    onChange={(e) => setMessageText(e.target.value)}
                                                    placeholder="Scrie mesajul tău aici... (ex: Este disponibil produsul? Acceptați negocierea?)"
                                                    rows={4}
                                                    data-testid="seller-message-input"
                                                />
                                            </div>
                                            <DialogFooter>
                                                <Button
                                                    onClick={handleSendMessageToSeller}
                                                    disabled={sendingSellerMessage || !messageText.trim()}
                                                    className="rounded-full bg-primary"
                                                    data-testid="send-seller-message"
                                                >
                                                    {sendingSellerMessage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                                                    Trimite mesaj
                                                </Button>
                                            </DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                )}
                                
                                {/* Login prompt for non-authenticated users */}
                                {!isOwner && !isAuthenticated && (
                                    <Button 
                                        className="w-full rounded-full text-sm h-8" 
                                        variant="outline"
                                        onClick={() => navigate('/login')}
                                    >
                                        <Send className="w-3 h-3 mr-2" />
                                        Autentifică-te pentru a trimite mesaj
                                    </Button>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Bid History - Full width for auctions */}
                {isAuction && (
                    <Card className="rounded-2xl mt-8">
                        <CardHeader>
                            <CardTitle className="font-heading">Istoric licitații</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {bids.length === 0 ? (
                                <p className="text-muted-foreground text-center py-4">
                                    Nicio licitație încă
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {bids.slice(0, 10).map((bid, index) => (
                                        <Link
                                            to={`/user/${bid.bidder_username}`}
                                            key={bid.bid_id}
                                            className={`flex items-center justify-between p-3 rounded-xl transition-colors hover:ring-2 hover:ring-primary/20 ${
                                                index === 0 ? 'bg-green-50 hover:bg-green-100' : 'bg-gray-50 hover:bg-gray-100'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <Avatar className="w-8 h-8">
                                                    {bid.bidder_avatar ? (
                                                        <AvatarImage src={bid.bidder_avatar} alt={bid.bidder_name || bid.bidder_username} />
                                                    ) : null}
                                                    <AvatarFallback className="text-xs">
                                                        {getInitials(bid.bidder_name || bid.bidder_username)}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div>
                                                    <p className="text-sm font-medium hover:text-primary">{bid.bidder_name || bid.bidder_username}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {formatDateTime(bid.created_at)}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="font-semibold">{formatCurrency(bid.amount)}</p>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default ListingDetailPage;
